import { useState } from "react";
import { FieldValue } from "../blocks/fields/FieldValue";
import { SearchableSelect } from "../blocks/fields/SearchableSelect";
import { Button } from "../elements/Button";
import { Loading } from "../elements/Loading";
import { GroupType, NodePath, SearchNode } from "./advancedSearch.types";
import { getRuleFieldOptions } from "./advancedSearchFields";
import { createRule } from "./advancedSearchRules";
import {
  addNode,
  canGroup,
  groupNodes,
  removeNode,
  ungroupNode,
  updateNode,
} from "./advancedSearchTree";
import { RuleTree, pathKey } from "./RuleTree";
import { RuleBuilderProps } from "./RuleBuilder.types";

const toPath = (key: string): NodePath => key.split("-").map(Number);

/** Builds the rules of a search: add a rule on a field, edit it, group rules with AND or OR. */
export function RuleBuilder({
  entityName,
  allowedEntityTypes,
  nodes,
  errors,
  fieldsReady,
  plain = false,
  onChange,
}: RuleBuilderProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [includeAll, setIncludeAll] = useState(false);

  const options = getRuleFieldOptions(entityName, allowedEntityTypes, includeAll);
  const groupable = canGroup(selected.map(toPath));

  const change = (next: SearchNode[]) => {
    setSelected([]);
    onChange(next);
  };

  const group = (groupType: GroupType) =>
    change(groupNodes(nodes, selected.map(toPath), groupType));

  const toggle = (path: NodePath) =>
    setSelected((current) =>
      current.includes(pathKey(path))
        ? current.filter((key) => key !== pathKey(path))
        : [...current, pathKey(path)]
    );

  const addRule = (fieldKey: string) => {
    const rule = fieldKey ? createRule(entityName, fieldKey, allowedEntityTypes) : null;

    if (rule) {
      onChange(addNode(nodes, rule));
    }
  };

  return (
    <div className={plain ? undefined : "p-3 border rounded"}>
      <div className="d-flex gap-3 mb-3 align-items-center">
        <Button
          text="Group AND"
          variant="outline-secondary"
          disabled={!groupable}
          onClick={() => group("And")}
        />
        <Button
          text="Group OR"
          variant="outline-secondary"
          disabled={!groupable}
          onClick={() => group("Or")}
        />
        <span className="fs-7 text-muted">
          Tick two or more rules at the same level to group them.
        </span>
      </div>
      {nodes.length === 0 && (
        <p className="text-muted">No rules - every {entityName} record matches.</p>
      )}
      <RuleTree
        {...{ errors, nodes }}
        selected={selected}
        onSelect={toggle}
        onChange={(path, node) => onChange(updateNode(nodes, path, node))}
        onRemove={(path) => change(removeNode(nodes, path))}
        onUngroup={(path) => change(ungroupNode(nodes, path))}
      />
      <div className="row border-top pt-3">
        <div className="col-12 col-lg-6">
          {fieldsReady ? (
            <FieldValue label="Add Field" htmlFor="add-rule-field">
              <SearchableSelect
                id="add-rule-field"
                value={null}
                options={options.map(({ Key, Value }) => ({ value: Key, label: Value }))}
                placeholder="Select a field"
                onChange={addRule}
              />
            </FieldValue>
          ) : (
            <Loading />
          )}
          <div className="form-check mt-2">
            <input
              id="rule-include-all-fields"
              className="form-check-input cursor-pointer"
              type="checkbox"
              checked={includeAll}
              onChange={() => setIncludeAll((current) => !current)}
            />
            <label className="form-check-label cursor-pointer" htmlFor="rule-include-all-fields">
              Include all fields
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
